import { useId, useState } from 'react';
import { useOne, useCreate, useUpdate, useGo } from '@refinedev/core';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { Loader2, Tag, Users } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FormHeader, FormStickyActions, FormSpacer } from '@/components/forms';
import { notify } from '@/lib/notify';
import { PromoDiscountType, PromoTargetType } from '@masters-club/types';
import type { SegmentDefinition } from '@masters-club/validation';
import { SegmentBuilder, AudienceUsersTable } from '@/components/audience-segment';
import type { PromoCodeItem } from './types';
import { PromoTargetMultiSelect } from './target-multi-select';

const EMPTY_SEGMENT: SegmentDefinition = { groupsOp: 'AND', groups: [] };

interface PromoCodeFormData {
  code: string;
  discountType: PromoCodeItem['discountType'];
  value: string;
  targetType: PromoCodeItem['targetType'];
  targetIds: number[];
  maxActivations: string;
  maxActivationsPerUser: string;
  minOrderAmount: string;
  startsAt: string;
  endsAt: string;
  isActive: boolean;
  audienceSegment: SegmentDefinition;
}

function buildInitialState(item?: PromoCodeItem, isAuto = false): PromoCodeFormData {
  if (!item) {
    return {
      code: isAuto ? `AUTO_${Date.now()}` : '',
      discountType: PromoDiscountType.PERCENT,
      value: '',
      // Для автоскидок дефолтом ставим конкретный товар (а не ALL) — это естественнее: «-20% на курс X».
      targetType: isAuto ? PromoTargetType.COURSE : PromoTargetType.ALL,
      targetIds: [],
      maxActivations: '',
      maxActivationsPerUser: '',
      minOrderAmount: '',
      startsAt: '',
      endsAt: '',
      isActive: true,
      audienceSegment: EMPTY_SEGMENT,
    };
  }
  return {
    code: item.code,
    discountType: item.discountType,
    value: item.value ?? '',
    targetType: item.targetType,
    targetIds: item.targetIds ?? [],
    maxActivations: item.maxActivations !== null ? String(item.maxActivations) : '',
    maxActivationsPerUser: item.maxActivationsPerUser !== null ? String(item.maxActivationsPerUser) : '',
    minOrderAmount: item.minOrderAmount ?? '',
    startsAt: item.startsAt ? item.startsAt.slice(0, 10) : '',
    endsAt: item.endsAt ? item.endsAt.slice(0, 10) : '',
    isActive: item.isActive,
    audienceSegment: item.audienceSegment ?? EMPTY_SEGMENT,
  };
}

function errorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const { message } = error;
    if (typeof message === 'string' && message.length > 0) { return message; }
  }
  return fallback;
}

function parseOptionalInt(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) { return null; }
  const parsed = parseInt(trimmed, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseOptionalString(value: string): string | null {
  const trimmed = value.trim();
  return trimmed || null;
}

interface PromoCodesFormProps {
  mode?: 'codes' | 'auto-discounts';
}

export function PromoCodesForm({ mode = 'codes' }: PromoCodesFormProps = {}) {
  const { id } = useParams<{ id: string }>();
  const go = useGo();
  const isEdit = !!id;
  const isAuto = mode === 'auto-discounts';
  const routePrefix = isAuto ? '/discounts' : '/promo-codes';

  const oneResult = useOne<PromoCodeItem>({
    resource: 'promo-codes',
    id: id ? parseInt(id, 10) : undefined,
    queryOptions: { enabled: isEdit },
  });

  const itemData = oneResult.query.data?.data;
  const isLoadingData = oneResult.query.isLoading;

  if (isEdit && isLoadingData) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const formKey = isEdit ? `edit-${itemData?.id ?? 'loading'}` : 'create';

  return (
    <PromoCodesFormFields
      key={formKey}
      isEdit={isEdit}
      id={id}
      isAuto={isAuto}
      initialState={buildInitialState(itemData, isAuto)}
      onBack={() => go({ to: routePrefix })}
    />
  );
}

interface PromoCodesFormFieldsProps {
  isEdit: boolean;
  id?: string;
  isAuto: boolean;
  initialState: PromoCodeFormData;
  onBack: () => void;
}

function PromoCodesFormFields({ isEdit, id, isAuto, initialState, onBack }: PromoCodesFormFieldsProps) {
  const go = useGo();
  const formId = useId();
  const [formData, setFormData] = useState<PromoCodeFormData>(initialState);

  const createMutation = useCreate<PromoCodeItem>();
  const updateMutation = useUpdate<PromoCodeItem>();

  const isSubmitting = createMutation.mutation.isPending || updateMutation.mutation.isPending;
  const hasAudienceSegment = formData.audienceSegment.groups.length > 0;

  const handleChange = <K extends keyof PromoCodeFormData>(field: K, value: PromoCodeFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const routePrefix = isAuto ? '/discounts' : '/promo-codes';

  const validate = (): boolean => {
    if (!isAuto && !formData.code.trim()) {
      notify.error('Введите код промокода');
      return false;
    }
    if (!formData.value.trim()) {
      notify.error('Введите значение скидки');
      return false;
    }
    if (!/^\d+(\.\d{1,2})?$/.test(formData.value.trim())) {
      notify.error('Значение скидки должно быть числом (например 10 или 9.99)');
      return false;
    }
    return true;
  };

  const submitData = () => {
    if (!validate()) { return; }

    const trimmedCode = formData.code.trim().toUpperCase();
    const values = {
      // Для автоскидок код невидим пользователю, но обязателен в БД — генерируем технический.
      code: trimmedCode || `AUTO_${Date.now()}`,
      discountType: formData.discountType,
      value: formData.value.trim(),
      targetType: formData.targetType,
      targetIds: formData.targetIds.length > 0 ? formData.targetIds : null,
      maxActivations: parseOptionalInt(formData.maxActivations),
      maxActivationsPerUser: parseOptionalInt(formData.maxActivationsPerUser),
      minOrderAmount: parseOptionalString(formData.minOrderAmount),
      startsAt: parseOptionalString(formData.startsAt),
      endsAt: parseOptionalString(formData.endsAt),
      isActive: formData.isActive,
      autoApply: isAuto,
      audienceSegment: hasAudienceSegment ? formData.audienceSegment : null,
    };

    if (isEdit && id) {
      updateMutation.mutate(
        { resource: 'promo-codes', id: parseInt(id, 10), values },
        {
          onSuccess: () => {
            go({ to: routePrefix });
          },
          onError: (error) => {
            notify.error(errorMessage(error, 'Ошибка обновления'));
          },
        },
      );
    } else {
      createMutation.mutate(
        { resource: 'promo-codes', values },
        {
          onSuccess: () => {
            go({ to: routePrefix });
          },
          onError: (error) => {
            notify.error(errorMessage(error, 'Ошибка создания'));
          },
        },
      );
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitData();
  };

  return (
    <div className="space-y-6">
      <FormHeader
        title={isEdit
          ? (isAuto ? 'Редактировать скидку' : 'Редактировать промокод')
          : (isAuto ? 'Создать скидку' : 'Создать промокод')}
        description="Промокоды для скидок при оформлении заказов"
        sticky
        icon={<Tag className="h-5 w-5" />}
        onBack={onBack}
      />

      <form id={formId} onSubmit={handleFormSubmit} className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 min-w-0 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Ограничение по аудитории</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground mb-4">
                Оставьте пустым — промокод будет доступен всем пользователям. При добавлении условий промокод будет доступен только соответствующим пользователям.
              </p>
              <SegmentBuilder
                value={formData.audienceSegment}
                onChange={(value) => handleChange('audienceSegment', value)}
                disabled={isSubmitting}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                На кого будет работать
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground mb-4">
                {hasAudienceSegment
                  ? 'Предпросмотр пользователей, удовлетворяющих условиям сегмента. Только они смогут применить промокод.'
                  : 'Без условий промокод доступен всем пользователям. Список ниже — все пользователи системы.'}
              </p>
              <AudienceUsersTable
                previewUrl="/audience/preview"
                definition={formData.audienceSegment}
                disabled={isSubmitting}
                showMatchColumn={hasAudienceSegment}
                matchColumnHeader="Промокод сработает"
                matchTrueLabel="Сработает"
                matchFalseLabel="Не сработает"
              />
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6 min-w-0 lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>Основные параметры</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!isAuto && (
                <div className="space-y-2">
                  <Label htmlFor={`${formId}-code`}>Код промокода *</Label>
                  <Input
                    id={`${formId}-code`}
                    placeholder="SUMMER20"
                    value={formData.code}
                    onChange={(e) => handleChange('code', e.target.value.toUpperCase())}
                    className="font-mono uppercase"
                  />
                  <p className="text-xs text-muted-foreground">
                    Код автоматически приводится к верхнему регистру
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Тип скидки *</Label>
                  <Select
                    value={formData.discountType}
                    onValueChange={(v) => handleChange('discountType', v as PromoCodeFormData['discountType'])}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={PromoDiscountType.PERCENT}>Процент (%)</SelectItem>
                      <SelectItem value={PromoDiscountType.FIXED}>Фиксированная сумма</SelectItem>
                      <SelectItem value={PromoDiscountType.PRICE_OVERRIDE}>Замена цены</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor={`${formId}-value`}>
                    Значение *
                    {formData.discountType === PromoDiscountType.PERCENT && ' (%)'}
                  </Label>
                  <Input
                    id={`${formId}-value`}
                    placeholder={formData.discountType === PromoDiscountType.PERCENT ? '20' : '500'}
                    value={formData.value}
                    onChange={(e) => handleChange('value', e.target.value)}
                    type="text"
                    inputMode="decimal"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between rounded-lg border p-4">
                <div>
                  <p className="text-sm font-medium">Активен</p>
                  <p className="text-xs text-muted-foreground">Промокод можно применять</p>
                </div>
                <Switch
                  checked={formData.isActive}
                  onCheckedChange={(checked) => handleChange('isActive', checked)}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Применимость</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Целевой тип</Label>
                <Select
                  value={formData.targetType}
                  onValueChange={(v) => {
                    const next = v as PromoCodeFormData['targetType'];
                    setFormData((prev) => ({ ...prev, targetType: next, targetIds: [] }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={PromoTargetType.ALL}>Все товары</SelectItem>
                    <SelectItem value={PromoTargetType.COURSE}>Курс</SelectItem>
                    <SelectItem value={PromoTargetType.CHANNEL_PASS}>Подписка в канал</SelectItem>
                    <SelectItem value={PromoTargetType.SUBSCRIPTION_PLAN}>Абонемент</SelectItem>
                    <SelectItem value={PromoTargetType.BOOK}>Книга</SelectItem>
                    <SelectItem value={PromoTargetType.PRICING_TIER}>Конкретный тариф</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.targetType !== PromoTargetType.ALL && (
                <div className="space-y-2">
                  <Label>Товары</Label>
                  <PromoTargetMultiSelect
                    targetType={formData.targetType}
                    value={formData.targetIds}
                    onChange={(ids) => handleChange('targetIds', ids)}
                    disabled={isSubmitting}
                  />
                  <p className="text-xs text-muted-foreground">
                    Оставьте пустым для применения ко всем товарам выбранного типа
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor={`${formId}-minOrderAmount`}>Минимальная сумма заказа</Label>
                <Input
                  id={`${formId}-minOrderAmount`}
                  placeholder="1000"
                  value={formData.minOrderAmount}
                  onChange={(e) => handleChange('minOrderAmount', e.target.value)}
                  type="text"
                  inputMode="decimal"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Ограничения активаций</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor={`${formId}-maxActivations`}>Макс. активаций всего</Label>
                <Input
                  id={`${formId}-maxActivations`}
                  placeholder="Не ограничено"
                  value={formData.maxActivations}
                  onChange={(e) => handleChange('maxActivations', e.target.value)}
                  type="number"
                  min="1"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor={`${formId}-maxActivationsPerUser`}>Макс. на одного пользователя</Label>
                <Input
                  id={`${formId}-maxActivationsPerUser`}
                  placeholder="Не ограничено"
                  value={formData.maxActivationsPerUser}
                  onChange={(e) => handleChange('maxActivationsPerUser', e.target.value)}
                  type="number"
                  min="1"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Срок действия</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Начало действия</Label>
                <DatePicker
                  value={formData.startsAt ? new Date(formData.startsAt) : null}
                  onChange={(date) =>
                    handleChange('startsAt', date ? format(date, 'yyyy-MM-dd') : '')
                  }
                  placeholder="Не задано"
                />
              </div>

              <div className="space-y-2">
                <Label>Конец действия</Label>
                <DatePicker
                  value={formData.endsAt ? new Date(formData.endsAt) : null}
                  onChange={(date) =>
                    handleChange('endsAt', date ? format(date, 'yyyy-MM-dd') : '')
                  }
                  placeholder="Бессрочно"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Оставьте пустым для бессрочного промокода
              </p>
            </CardContent>
          </Card>
        </div>
      </form>

      <FormSpacer />

      <FormStickyActions
        primaryLabel={isEdit ? 'Сохранить' : 'Создать'}
        secondaryLabel="Отмена"
        onPrimary={submitData}
        onSecondary={onBack}
        isPending={isSubmitting}
        pendingLabel={isEdit ? 'Сохранение...' : 'Создание...'}
      />
    </div>
  );
}
